import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRuler, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const QuiltCalculatorPreview = () => {
    const navigate = useNavigate();
    const [quiltDimensions, setQuiltDimensions] = useState({
        quiltWidth: 4,
        quiltHeight: 4,
        tileWidth: 12,
        tileHeight: 12,
        columns: 4,
        rows: 4,
        totalTiles: 16,
        tileWidthPixels: 300,
        tileHeightPixels: 300
    });
    const [showWarning, setShowWarning] = useState(false);
    const [gridSize, setGridSize] = useState('4x4');
    const [requiredImages, setRequiredImages] = useState(16);
    const [errors, setErrors] = useState({
        quiltWidth: '',
        quiltHeight: '',
        tileWidth: '',
        tileHeight: ''
    });

    // Input values
    const [quiltWidthInput, setQuiltWidthInput] = useState('4');
    const [quiltHeightInput, setQuiltHeightInput] = useState('4');
    const [tileWidthInput, setTileWidthInput] = useState('12');
    const [tileHeightInput, setTileHeightInput] = useState('12');

    // Limits for quilt and tile sizes
    const quiltSizeLimits = { min: 4, max: 8 }; // feet
    const tileSizeLimits = { min: 6, max: 15 }; // inches

    // Validation functions
    const validateQuiltWidth = (value) => {
        if (!value) return { valid: false, error: 'Width is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        if (numericValue < quiltSizeLimits.min) 
            return { valid: false, error: `Min ${quiltSizeLimits.min} feet` };
        if (numericValue > quiltSizeLimits.max) 
            return { valid: false, error: `Max ${quiltSizeLimits.max} feet` };
        
        if (quiltDimensions.quiltHeight && numericValue > quiltDimensions.quiltHeight) 
            return { valid: false, error: `Width can't exceed height` };
            
        return { valid: true, error: '', value: numericValue };
    };
    
    const validateQuiltHeight = (value) => {
        if (!value) return { valid: false, error: 'Height is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        if (numericValue < quiltSizeLimits.min) 
            return { valid: false, error: `Min ${quiltSizeLimits.min} feet` };
        if (numericValue > quiltSizeLimits.max) 
            return { valid: false, error: `Max ${quiltSizeLimits.max} feet` };
        
        return { valid: true, error: '', value: numericValue };
    };
    
    const validateTileWidth = (value) => {
        if (!value) return { valid: false, error: 'Width is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        let dynamicMinTileSize = tileSizeLimits.min;
        if (quiltDimensions.quiltHeight >= 8) {
            dynamicMinTileSize = 8;
        } else if (quiltDimensions.quiltHeight >= 7) {
            dynamicMinTileSize = 7;
        }
        
        if (numericValue < dynamicMinTileSize) 
            return { valid: false, error: `Min ${dynamicMinTileSize} inches` };
        if (numericValue > tileSizeLimits.max) 
            return { valid: false, error: `Max ${tileSizeLimits.max} inches` };
        
        const maxWidth = quiltDimensions.quiltWidth ? (quiltDimensions.quiltWidth * 12) / 4 : 24;
        if (numericValue > maxWidth) 
            return { valid: false, error: `Too large for quilt width` };
        
        if (quiltDimensions.tileHeight && numericValue > quiltDimensions.tileHeight) 
            return { valid: false, error: `Width can't exceed height` };
        
        return { valid: true, error: '', value: numericValue };
    };
    
    const validateTileHeight = (value) => {
        if (!value) return { valid: false, error: 'Height is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        let dynamicMinTileSize = tileSizeLimits.min;
        if (quiltDimensions.quiltHeight >= 8) {
            dynamicMinTileSize = 8;
        } else if (quiltDimensions.quiltHeight >= 7) {
            dynamicMinTileSize = 7;
        }
        
        if (numericValue < dynamicMinTileSize) 
            return { valid: false, error: `Min ${dynamicMinTileSize} inches` };
        if (numericValue > tileSizeLimits.max) 
            return { valid: false, error: `Max ${tileSizeLimits.max} inches` };
        
        const maxHeight = quiltDimensions.quiltHeight ? (quiltDimensions.quiltHeight * 12) / 4 : 24;
        if (numericValue > maxHeight) 
            return { valid: false, error: `Too large for quilt height` };
        
        return { valid: true, error: '', value: numericValue };
    };

    // Input handlers
    const handleQuiltWidthChange = (e) => {
        const inputValue = e.target.value;
        setQuiltWidthInput(inputValue);
        
        if (!inputValue) {
            setQuiltDimensions(prev => ({ ...prev, quiltWidth: null }));
            setErrors(prev => ({ ...prev, quiltWidth: 'Width is required' }));
            return;
        }
        
        const validation = validateQuiltWidth(inputValue);
        setErrors(prev => ({ ...prev, quiltWidth: validation.error }));
        
        if (validation.valid) {
            setQuiltDimensions(prev => ({ ...prev, quiltWidth: validation.value }));
        } else {
            setQuiltDimensions(prev => ({ ...prev, quiltWidth: null }));
        }
    };

    const handleQuiltHeightChange = (e) => {
        const inputValue = e.target.value;
        setQuiltHeightInput(inputValue);
        
        if (!inputValue) {
            setQuiltDimensions(prev => ({ ...prev, quiltHeight: null }));
            setErrors(prev => ({ ...prev, quiltHeight: 'Height is required' }));
            return;
        }
        
        const validation = validateQuiltHeight(inputValue);
        setErrors(prev => ({ ...prev, quiltHeight: validation.error }));
        
        if (validation.valid) {
            setQuiltDimensions(prev => ({ ...prev, quiltHeight: validation.value }));
        } else {
            setQuiltDimensions(prev => ({ ...prev, quiltHeight: null }));
        }
    };

    const handleTileWidthChange = (e) => {
        const inputValue = e.target.value;
        setTileWidthInput(inputValue);
        
        if (!inputValue) {
            setQuiltDimensions(prev => ({ ...prev, tileWidth: null }));
            setErrors(prev => ({ ...prev, tileWidth: 'Width is required' }));
            return;
        }
        
        const validation = validateTileWidth(inputValue);
        setErrors(prev => ({ ...prev, tileWidth: validation.error }));
        
        if (validation.valid) {
            setQuiltDimensions(prev => ({ ...prev, tileWidth: validation.value }));
        } else {
            setQuiltDimensions(prev => ({ ...prev, tileWidth: null }));
        }
    };

    const handleTileHeightChange = (e) => {
        const inputValue = e.target.value;
        setTileHeightInput(inputValue);
        
        if (!inputValue) {
            setQuiltDimensions(prev => ({ ...prev, tileHeight: null }));
            setErrors(prev => ({ ...prev, tileHeight: 'Height is required' }));
            return;
        }
        
        const validation = validateTileHeight(inputValue);
        setErrors(prev => ({ ...prev, tileHeight: validation.error }));
        
        if (validation.valid) {
            setQuiltDimensions(prev => ({ ...prev, tileHeight: validation.value }));
        } else {
            setQuiltDimensions(prev => ({ ...prev, tileHeight: null }));
        }
    };

    // Calculate grid size whenever dimensions change
    useEffect(() => {
        if (quiltDimensions.quiltWidth && quiltDimensions.quiltHeight && 
            quiltDimensions.tileWidth && quiltDimensions.tileHeight) {
            
            // Convert feet to inches
            const quiltWidthInches = quiltDimensions.quiltWidth * 12;
            const quiltHeightInches = quiltDimensions.quiltHeight * 12;
            
            // Calculate number of tiles
            const columns = Math.floor(quiltWidthInches / quiltDimensions.tileWidth);
            const rows = Math.floor(quiltHeightInches / quiltDimensions.tileHeight);
            
            // Enforce minimum grid size
            const finalColumns = Math.max(columns, 4);
            const finalRows = Math.max(rows, 4);
            
            // Create grid size string and update total tiles
            const newGridSize = `${finalColumns}x${finalRows}`;
            const calculatedTotalTiles = finalColumns * finalRows;
            
            setGridSize(newGridSize);
            setRequiredImages(calculatedTotalTiles);
            
            // Update quilt dimensions with calculated grid
            setQuiltDimensions(prev => ({
                ...prev,
                columns: finalColumns,
                rows: finalRows,
                totalTiles: calculatedTotalTiles
            }));
        }
    }, [quiltDimensions.quiltWidth, quiltDimensions.quiltHeight, quiltDimensions.tileWidth, quiltDimensions.tileHeight]);

    // Generate tile colors for the visual grid - updated to handle rectangular tiles correctly
    const generateTileColors = () => {
        if (!quiltDimensions.columns || !quiltDimensions.rows) {
            return [];
        }
        
        const colors = ['bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-red-100', 'bg-purple-100', 'bg-pink-100'];
        const tiles = [];
        
        // Calculate aspect ratio for each tile
        const aspectRatio = quiltDimensions.tileWidth / quiltDimensions.tileHeight;
        
        for (let i = 0; i < quiltDimensions.rows; i++) {
            for (let j = 0; j < quiltDimensions.columns; j++) {
                const colorIndex = (i + j) % colors.length;
                tiles.push(
                    <div 
                        key={`${i}-${j}`} 
                        className={`border border-gray-300 ${colors[colorIndex]}`}
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            // Ensure the aspect ratio is visually maintained
                            aspectRatio: aspectRatio
                        }}
                    />
                );
            }
        }
        
        return tiles;
    };

    // Calculate border dimensions
    const calculateBorders = () => {
        if (!quiltDimensions.quiltWidth || !quiltDimensions.quiltHeight || 
            !quiltDimensions.tileWidth || !quiltDimensions.tileHeight) {
            return { leftoverWidth: 0, leftoverHeight: 0 };
        }

        const widthInches = quiltDimensions.quiltWidth * 12;
        const heightInches = quiltDimensions.quiltHeight * 12;
        
        const usedWidth = quiltDimensions.columns * quiltDimensions.tileWidth;
        const usedHeight = quiltDimensions.rows * quiltDimensions.tileHeight;
        
        const leftoverWidth = widthInches - usedWidth;
        const leftoverHeight = heightInches - usedHeight;
        
        return { leftoverWidth, leftoverHeight };
    };
    
    const { leftoverWidth, leftoverHeight } = calculateBorders();
    
    // Validation for dimensions
    const isQuiltDimensionsValid = () => {
        return quiltDimensions.quiltWidth && 
               quiltDimensions.quiltHeight && 
               quiltDimensions.tileWidth && 
               quiltDimensions.tileHeight &&
               !errors.quiltWidth &&
               !errors.quiltHeight &&
               !errors.tileWidth &&
               !errors.tileHeight;
    };

    return (
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-md">
            <p className="text-center text-gray-600 mb-6">
                Calculate how many clothing items you'll need for your memory quilt. 
                Adjust the dimensions below to see how your quilt layout will change.
            </p>
            
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Form Controls */}
                <div className="flex-1">
                    {/* Quilt Dimensions Box */}
                    <div className="mb-6">
                        <div className="bg-[#F0F4ED] p-4 rounded-lg border border-[#DCE8DB]">
                            <h3 className="text-lg font-semibold text-[#7A8B73] mb-3" style={{ fontFamily: "Playfair Display" }}>
                                Quilt Dimensions
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                {/* Quilt Width */}
                                <div className="flex flex-col">
                                    <label className="text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: "Playfair Display" }}>
                                        Quilt Width (feet)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="quiltWidth"
                                            value={quiltWidthInput}
                                            onChange={handleQuiltWidthChange}
                                            placeholder={`${quiltSizeLimits.min}-${quiltSizeLimits.max} feet`}
                                            className={`border rounded-md p-2 text-gray-800 w-full ${errors.quiltWidth ? 'border-red-500' : 'border-[#9CAF88]'}`}
                                        />
                                        {errors.quiltWidth && (
                                            <p className="text-red-500 text-xs mt-1">{errors.quiltWidth}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Quilt Height */}
                                <div className="flex flex-col">
                                    <label className="text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: "Playfair Display" }}>
                                        Quilt Height (feet)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="quiltHeight"
                                            value={quiltHeightInput}
                                            onChange={handleQuiltHeightChange}
                                            placeholder={`${quiltSizeLimits.min}-${quiltSizeLimits.max} feet`}
                                            className={`border rounded-md p-2 text-gray-800 w-full ${errors.quiltHeight ? 'border-red-500' : 'border-[#9CAF88]'}`}
                                        />
                                        {errors.quiltHeight && (
                                            <p className="text-red-500 text-xs mt-1">{errors.quiltHeight}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tile Dimensions Box */}
                    <div className="mb-6">
                        <div className="bg-[#F0F4ED] p-4 rounded-lg border border-[#DCE8DB]">
                            <h3 className="text-lg font-semibold text-[#7A8B73] mb-3" style={{ fontFamily: "Playfair Display" }}>
                                Tile Dimensions
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                {/* Tile Width */}
                                <div className="flex flex-col">
                                    <label className="text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: "Playfair Display" }}>
                                        Tile Width (inches)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="tileWidth"
                                            value={tileWidthInput}
                                            onChange={handleTileWidthChange}
                                            placeholder={`${tileSizeLimits.min}-${tileSizeLimits.max} inches`}
                                            className={`border rounded-md p-2 text-gray-800 w-full ${errors.tileWidth ? 'border-red-500' : 'border-[#9CAF88]'}`}
                                        />
                                        {errors.tileWidth && (
                                            <p className="text-red-500 text-xs mt-1">{errors.tileWidth}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Tile Height */}
                                <div className="flex flex-col">
                                    <label className="text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: "Playfair Display" }}>
                                        Tile Height (inches)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="tileHeight"
                                            value={tileHeightInput}
                                            onChange={handleTileHeightChange}
                                            placeholder={`${tileSizeLimits.min}-${tileSizeLimits.max} inches`}
                                            className={`border rounded-md p-2 text-gray-800 w-full ${errors.tileHeight ? 'border-red-500' : 'border-[#9CAF88]'}`}
                                        />
                                        {errors.tileHeight && (
                                            <p className="text-red-500 text-xs mt-1">{errors.tileHeight}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Right Column - Preview */}
                <div className="flex-1">
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center" style={{ fontFamily: "Playfair Display" }}>
                            Quilt Layout Preview
                        </h3>
                        <div className="flex justify-center">
                            <div className="border-2 border-gray-400 rounded-md p-2 bg-white">
                                {isQuiltDimensionsValid() ? (
                                    <div className="relative">
                                        {/* Container with proper aspect ratio for the quilt */}
                                        <div 
                                            style={{
                                                width: '240px', 
                                                height: `${240 * (quiltDimensions.quiltHeight / quiltDimensions.quiltWidth)}px`,
                                                maxHeight: '300px',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                backgroundColor: '#f8f8f8',
                                                border: '1px solid #ddd'
                                            }}
                                        >
                                            {/* Center the grid in the container - fixed for non-square tiles */}
                                            <div 
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${quiltDimensions.columns}, ${quiltDimensions.tileWidth}fr)`,
                                                    gridTemplateRows: `repeat(${quiltDimensions.rows}, ${quiltDimensions.tileHeight}fr)`,
                                                    gap: '2px',
                                                    width: `${((quiltDimensions.columns * quiltDimensions.tileWidth) / (quiltDimensions.quiltWidth * 12)) * 100}%`,
                                                    height: `${((quiltDimensions.rows * quiltDimensions.tileHeight) / (quiltDimensions.quiltHeight * 12)) * 100}%`,
                                                    position: 'absolute',
                                                    top: `${(leftoverHeight / (quiltDimensions.quiltHeight * 12) / 2) * 100}%`,
                                                    left: `${(leftoverWidth / (quiltDimensions.quiltWidth * 12) / 2) * 100}%`
                                                }}
                                            >
                                                {generateTileColors()}
                                            </div>
                                            
                                            {/* Visual indicators for unused space */}
                                            {leftoverWidth > 0 && (
                                                <>
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            left: '0',
                                                            top: '0',
                                                            width: `${(leftoverWidth / (quiltDimensions.quiltWidth * 12) / 2) * 100}%`,
                                                            height: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderRight: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            right: '0',
                                                            top: '0',
                                                            width: `${(leftoverWidth / (quiltDimensions.quiltWidth * 12) / 2) * 100}%`,
                                                            height: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderLeft: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                </>
                                            )}
                                            {leftoverHeight > 0 && (
                                                <>
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            top: '0',
                                                            left: '0',
                                                            height: `${(leftoverHeight / (quiltDimensions.quiltHeight * 12) / 2) * 100}%`,
                                                            width: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderBottom: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '0',
                                                            left: '0',
                                                            height: `${(leftoverHeight / (quiltDimensions.quiltHeight * 12) / 2) * 100}%`,
                                                            width: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderTop: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-gray-500 text-sm p-8 text-center" style={{ width: '240px', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        Enter valid dimensions to see preview
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Layout summary */}
                    {isQuiltDimensionsValid() && (
                        <div className="mt-4 bg-[#F8F9FA] border border-[#9CAF88] p-3 rounded-md">
                            <div className="font-semibold text-center text-[#7A8B73] mb-2" style={{ fontFamily: "Playfair Display" }}>
                                Layout Summary
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="text-sm">
                                    <span className="font-medium">Grid Size:</span> {gridSize}
                                </div>
                                <div className="text-sm">
                                    <span className="font-medium">Required Images:</span> {requiredImages}
                                </div>
                                <div className="text-sm">
                                    <span className="font-medium">Quilt Size:</span> {quiltDimensions.quiltWidth}' × {quiltDimensions.quiltHeight}'
                                </div>
                                <div className="text-sm">
                                    <span className="font-medium">Tile Size:</span> {quiltDimensions.tileWidth}" × {quiltDimensions.tileHeight}"
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Start button */}
            <div className="mt-6 flex justify-center">
                <button
                    onClick={() => navigate('/sign-in')}
                    className="bg-[#9CAF88] hover:bg-[#8A9B79] text-white px-6 py-2 rounded-lg flex items-center shadow-md"
                    style={{ fontFamily: "Playfair Display" }}
                >
                    Start Creating Your Memory Quilt <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
            </div>
            
            <p className="text-center text-sm text-gray-500 mt-4">
                This is a preview of our quilt calculator. Start the process to design your own memory quilt and preserve your cherished memories.
            </p>
        </div>
    );
};

export default QuiltCalculatorPreview;
